// League Dashboard - Main league view with competitions, members, game section
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLeague } from '../contexts/LeagueContext';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Avatar, Input } from '../components';
import { linkCompetitionToLeague } from '../services/leagueService';

// DiceFace component - renders a realistic looking dice
const DiceFace = ({ value, size = 32 }) => {
  const dotSize = size * 0.18;

  // Dot positions for each dice value
  const dotPositions = {
    1: [[0.5, 0.5]],
    2: [[0.25, 0.25], [0.75, 0.75]],
    3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
    4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
    5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
    6: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.5], [0.75, 0.5], [0.25, 0.75], [0.75, 0.75]]
  };

  const dots = dotPositions[value] || [];

  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: '#EF4444',
        borderRadius: size * 0.2,
        position: 'relative',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.2)',
        border: '2px solid #B91C1C',
        display: 'inline-block',
        flexShrink: 0
      }}
    >
      {dots.map((pos, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: dotSize,
            height: dotSize,
            backgroundColor: 'white',
            borderRadius: '50%',
            left: `calc(${pos[0] * 100}% - ${dotSize / 2}px)`,
            top: `calc(${pos[1] * 100}% - ${dotSize / 2}px)`,
            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
          }}
        />
      ))}
    </div>
  );
};

const LeagueDashboard = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentLeague,
    leagueCompetitions,
    leagueGameSections,
    loading,
    error,
    loadLeague,
    isLeagueAdmin,
    generateInviteLink
  } = useLeague();

  const [activeTab, setActiveTab] = useState('competitions');
  const [inviteLink, setInviteLink] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Link competition state
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [competitionIdToLink, setCompetitionIdToLink] = useState('');
  const [linkingCompetition, setLinkingCompetition] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    if (leagueId) {
      loadLeague(leagueId);
    }
  }, [leagueId, loadLeague]);

  const handleGenerateInviteLink = async () => {
    setGeneratingLink(true);
    try {
      const link = await generateInviteLink();
      setInviteLink(link);
    } catch (err) {
      console.error('Error generating invite link:', err);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLinkCompetition = async () => {
    if (!competitionIdToLink.trim()) {
      setLinkError('Please enter a competition ID');
      return;
    }

    setLinkingCompetition(true);
    setLinkError('');
    setLinkResult(null);

    try {
      const result = await linkCompetitionToLeague(leagueId, competitionIdToLink.trim());
      setLinkResult(result);
      setCompetitionIdToLink('');
      // Refresh league data to show updated competitions and members
      loadLeague(leagueId);
    } catch (err) {
      console.error('Error linking competition:', err);
      setLinkError(err.message || 'Failed to link competition');
    } finally {
      setLinkingCompetition(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading league...</p>
        </div>
      </div>
    );
  }

  if (error || !currentLeague) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">League Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'This league does not exist or you do not have access.'}</p>
          <Button onClick={() => navigate('/leagues')}>Back to Leagues</Button>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'competitions', label: 'Competitions', icon: '🏆' },
    { id: 'games', label: 'Game Library', icon: '🎮' },
    { id: 'members', label: 'Members', icon: '👥' },
    { id: 'halloffame', label: 'Hall of Fame', icon: '🏅' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/leagues')}>
                ← Back
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{currentLeague.name}</h1>
            {currentLeague.description && (
              <p className="text-gray-600 mt-1">{currentLeague.description}</p>
            )}
          </div>
          {isLeagueAdmin && (
            <Button onClick={() => navigate(`/league/${leagueId}/settings`)}>
              Settings
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'competitions' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Competitions</h2>
              {isLeagueAdmin && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowLinkForm(!showLinkForm)}>
                    Link Existing
                  </Button>
                  <Button onClick={() => navigate(`/league/${leagueId}/create-competition`)}>
                    + New Competition
                  </Button>
                </div>
              )}
            </div>

            {/* Link Existing Competition Form */}
            {showLinkForm && isLeagueAdmin && (
              <Card className="mb-4 bg-blue-50 border-blue-200">
                <h3 className="font-bold text-blue-900 mb-2">Link Existing Competition</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Enter the competition ID to link it to this league. All players from the competition will be added as league members.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={competitionIdToLink}
                    onChange={(e) => setCompetitionIdToLink(e.target.value)}
                    placeholder="Competition ID (e.g., 1dd2cf7f-c188-4ca2-a66e-50b140ec793a)"
                    className="flex-1"
                  />
                  <Button onClick={handleLinkCompetition} disabled={linkingCompetition}>
                    {linkingCompetition ? 'Linking...' : 'Link'}
                  </Button>
                </div>
                {linkError && (
                  <p className="text-sm text-red-600 mt-2">{linkError}</p>
                )}
                {linkResult && (
                  <p className="text-sm text-green-600 mt-2">
                    Competition linked! Added {linkResult.playersAdded} new members (total: {linkResult.totalMembers}), {linkResult.gamesAdded || 0} games added to library.
                  </p>
                )}
              </Card>
            )}

            {leagueCompetitions.length === 0 ? (
              <Card className="text-center py-12">
                <div className="text-6xl mb-4">🏆</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No Competitions Yet</h3>
                <p className="text-gray-600 mb-4">
                  {isLeagueAdmin
                    ? 'Create your first competition to get started!'
                    : 'The league admin will create competitions soon.'}
                </p>
                {isLeagueAdmin && (
                  <Button onClick={() => navigate(`/league/${leagueId}/create-competition`)}>
                    Create Competition
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {leagueCompetitions
                  .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                  .map(competition => (
                    <Card
                      key={competition.id}
                      className="cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate(`/competition/${competition.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{competition.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {new Date(competition.startDate).toLocaleDateString()} - {new Date(competition.endDate).toLocaleDateString()}
                          </p>
                          <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                            competition.status === 'active' ? 'bg-green-100 text-green-700' :
                            competition.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {competition.status}
                          </span>
                        </div>
                      </div>
                    </Card>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'games' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Game Library</h2>
              <Button onClick={() => navigate(`/league/${leagueId}/games`)}>
                {isLeagueAdmin ? 'Manage Games' : 'View All Games'}
              </Button>
            </div>

            {leagueGameSections.length === 0 ? (
              <Card className="text-center py-12">
                <div className="text-6xl mb-4">🎮</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No Games Yet</h3>
                <p className="text-gray-600 mb-4">
                  Add games to your library to use them in competitions.
                </p>
                <Button onClick={() => navigate(`/league/${leagueId}/games`)}>
                  Add Games
                </Button>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Group and sort games by category */}
                {(() => {
                  const gamesByCategory = leagueGameSections.reduce((acc, game) => {
                    const cat = game.categoryName || 'Uncategorized';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(game);
                    return acc;
                  }, {});
                  const sortedCategories = Object.keys(gamesByCategory).sort();
                  sortedCategories.forEach(cat => {
                    gamesByCategory[cat].sort((a, b) => a.name.localeCompare(b.name));
                  });

                  return sortedCategories.map(category => (
                    <div key={category}>
                      <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                        <span>🎲</span> {category}
                        <span className="text-sm font-normal text-gray-500">
                          ({gamesByCategory[category].length})
                        </span>
                      </h3>
                      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {gamesByCategory[category].map(game => (
                          <Card key={game.id} className="overflow-hidden">
                            <div className="aspect-square bg-gray-100 flex items-center justify-center">
                              {game.photoUrl ? (
                                <img
                                  src={game.photoUrl}
                                  alt={game.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-6xl opacity-50">🎲</span>
                              )}
                            </div>
                            <div className="p-3">
                              <h4 className="font-bold text-gray-900">{game.name}</h4>
                              {/* Rating display with dice */}
                              <div className="flex items-center gap-2 mt-1">
                                {game.averageRating > 0 ? (
                                  <>
                                    <DiceFace value={Math.round(game.averageRating)} size={20} />
                                    <span className="text-sm font-bold text-gray-700">
                                      {game.averageRating.toFixed(1)}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      ({game.totalRatings || 0})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-400">No ratings</span>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                {game.averageDurationMinutes ? (
                                  <span className="text-xs text-gray-500">
                                    ~{game.averageDurationMinutes} min
                                  </span>
                                ) : (
                                  <span></span>
                                )}
                                {game.rulesUrl && (
                                  <a
                                    href={game.rulesUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span>📖</span> How to Play
                                  </a>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Members ({currentLeague.members?.length || 0})</h2>
              {isLeagueAdmin && (
                <Button onClick={handleGenerateInviteLink} disabled={generatingLink}>
                  {generatingLink ? 'Generating...' : 'Invite Members'}
                </Button>
              )}
            </div>

            {/* Invite Link */}
            {inviteLink && (
              <Card className="mb-4 bg-blue-50 border-blue-200">
                <h3 className="font-bold text-blue-900 mb-2">Invite Link</h3>
                <p className="text-sm text-blue-700 mb-2">Share this link to invite people to your league:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded text-sm"
                  />
                  <Button onClick={handleCopyLink} size="sm">
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-blue-600 mt-2">Link expires in 7 days</p>
              </Card>
            )}

            {/* Members List */}
            <Card>
              <div className="divide-y divide-gray-100">
                {currentLeague.members?.map(member => (
                  <div key={member.playerId} className="flex items-center gap-3 py-3">
                    <Avatar player={{ displayName: member.displayName }} size="md" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{member.displayName}</p>
                      <p className="text-xs text-gray-500">
                        Joined {new Date(member.joinedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {member.role === 'admin' && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        Admin
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'halloffame' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Hall of Fame</h2>
              <Button onClick={() => navigate(`/league/${leagueId}/hall-of-fame`)}>
                View Full Hall of Fame
              </Button>
            </div>
            <Card className="text-center py-12">
              <div className="text-6xl mb-4">🏆</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">League Champions</h3>
              <p className="text-gray-600 mb-4">
                Winners from all competitions in this league are recorded here.
              </p>
              <Button onClick={() => navigate(`/league/${leagueId}/hall-of-fame`)}>
                View Hall of Fame
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueDashboard;
